import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity, Platform } from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useFinalizeOfferPayment } from '@/data/offer-hooks';
import { appDataKeys } from '@/data/app-data-keys';
import { StatusBar } from 'expo-status-bar';
import { useCurrentTheme } from '@/theming/useCurrentTheme';
import { MaterialIcons } from '@expo/vector-icons';

export default function OfferPaymentSuccessScreen() {
    const {theme} = useCurrentTheme();
    const { t } = useTranslation();
    const { orgId, offerId } = useLocalSearchParams<{ orgId: string; offerId: string }>();
    const queryClient = useQueryClient();

    // Get query parameters
    const [purchaseId, setPurchaseId] = useState<string | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);

    // Parse URL parameters on mount
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            setPurchaseId(urlParams.get('purchaseId'));
            setToken(urlParams.get('token'));
            setSessionId(urlParams.get('sessionId'));
        }
    }, []);

    const { mutate: finalizePayment, isPending, data } = useFinalizeOfferPayment();

    // Finalize payment when we have the required parameters
    useEffect(() => {
        if (purchaseId && offerId && (token || sessionId)) {
            finalizePayment({
                offerId,
                purchaseId,
                token: token || undefined,
                sessionId: sessionId || undefined
            });
        }
    }, [purchaseId, offerId, token, sessionId, finalizePayment]);

    // Invalidate app data when payment is successful
    useEffect(() => {
        if (data?.status === 'completed') {
            queryClient.invalidateQueries({ queryKey: appDataKeys.detail() });
        }
    }, [data?.status, queryClient]);

    const handleContinue = () => {
        router.replace(`/organization/${orgId}/offers/${offerId}`);
    };

    const handleBackToOffers = () => {
        router.replace(`/organization/${orgId}/offers`);
    };

    const styles = StyleSheet.create({
        container: {
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20,
        },
        content: {
            alignItems: 'center',
            maxWidth: 400,
            width: '100%',
        },
        loadingText: {
            marginTop: 20,
            fontSize: 16,
        },
        title: {
            fontSize: 24,
            fontWeight: 'bold',
            marginTop: 20,
            marginBottom: 10,
            textAlign: 'center',
        },
        message: {
            fontSize: 16,
            textAlign: 'center',
            marginBottom: 30,
            lineHeight: 24,
        },
        buttonContainer: {
            width: '100%',
            gap: 10,
        },
        button: {
            minWidth: 200,
            paddingVertical: 12,
            paddingHorizontal: 24,
            borderRadius: 8,
            alignItems: 'center',
            justifyContent: 'center',
        },
        secondaryButton: {
            backgroundColor: 'transparent',
            borderWidth: 2,
        },
        buttonText: {
            fontSize: 16,
            fontWeight: '600',
        },
        secondaryButtonText: {
            color: theme.colors.primary,
        },
        spinner: {
            marginBottom: 20,
        },
    });

    // Loading state
    if (isPending || !data) {
        return (
            <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
                <StatusBar style="auto" />
                <ActivityIndicator size="large" color={theme.colors.primary} />
                <Text style={[styles.loadingText, { color: theme.colors.text }]}>
                    {t('offers.payment.processing')}
                </Text>
            </View>
        );
    }

    // Success state
    if (data.status === 'completed') {
        return (
            <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
                <StatusBar style="auto" />
                <View style={styles.content}>
                    <MaterialIcons
                        name="check-circle"
                        size={80}
                        color={theme.colors.success || '#4CAF50'}
                    />
                    <Text style={[styles.title, { color: theme.colors.text }]}>
                        {t('offers.payment.success.title')}
                    </Text>
                    <Text style={[styles.message, { color: theme.colors.textSecondary }]}>
                        {t('offers.payment.success.message')}
                    </Text>
                    <View style={styles.buttonContainer}>
                        <TouchableOpacity
                            onPress={handleContinue}
                            style={[styles.button, { backgroundColor: theme.colors.primary }]}
                        >
                            <Text style={styles.buttonText}>
                                {t('offers.payment.success.viewOffer')}
                            </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            onPress={handleBackToOffers}
                            style={[styles.button, styles.secondaryButton, { borderColor: theme.colors.primary }]}
                        >
                            <Text style={[styles.buttonText, styles.secondaryButtonText]}>
                                {t('offers.payment.success.backToOffers')}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        );
    }

    // Pending state
    if (data.status === 'pending') {
        return (
            <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
                <StatusBar style="auto" />
                <View style={styles.content}>
                    <MaterialIcons
                        name="schedule"
                        size={80}
                        color={theme.colors.warning || '#FF9800'}
                    />
                    <Text style={[styles.title, { color: theme.colors.text }]}>
                        {t('offers.payment.pending.title')}
                    </Text>
                    <Text style={[styles.message, { color: theme.colors.textSecondary }]}>
                        {data.message || t('offers.payment.pending.message')}
                    </Text>
                    <ActivityIndicator
                        size="small"
                        color={theme.colors.primary}
                        style={styles.spinner}
                    />
                    <View style={styles.buttonContainer}>
                        <TouchableOpacity
                            onPress={() => Platform.OS === 'web' && window.location.reload()}
                            style={[styles.button, { backgroundColor: theme.colors.primary }]}
                        >
                            <Text style={styles.buttonText}>
                                {t('offers.payment.pending.checkStatus')}
                            </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            onPress={handleBackToOffers}
                            style={[styles.button, styles.secondaryButton, { borderColor: theme.colors.primary }]}
                        >
                            <Text style={[styles.buttonText, styles.secondaryButtonText]}>
                                {t('offers.payment.success.backToOffers')}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        );
    }

    // Failed state
    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
            <StatusBar style="auto" />
            <View style={styles.content}>
                <MaterialIcons
                    name="error"
                    size={80}
                    color={theme.colors.error || '#F44336'}
                />
                <Text style={[styles.title, { color: theme.colors.text }]}>
                    {t('offers.payment.failed.title')}
                </Text>
                <Text style={[styles.message, { color: theme.colors.textSecondary }]}>
                    {data.message || t('offers.payment.failed.message')}
                </Text>
                <View style={styles.buttonContainer}>
                    <TouchableOpacity
                        onPress={handleContinue}
                        style={[styles.button, { backgroundColor: theme.colors.primary }]}
                    >
                        <Text style={styles.buttonText}>
                            {t('offers.payment.failed.tryAgain')}
                        </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={handleBackToOffers}
                        style={[styles.button, styles.secondaryButton, { borderColor: theme.colors.primary }]}
                    >
                        <Text style={[styles.buttonText, styles.secondaryButtonText]}>
                            {t('offers.payment.success.backToOffers')}
                        </Text>
                    </TouchableOpacity>
                </View>
            </View>
        </View>
    );
}
